import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IStoreState } from "..";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  getFileNameFromUrl,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import { getUniqueId } from "../../helpers";
import produce from "immer";
import { getFileExtensionType } from "../../components/FileUploadIcon/helpers";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";
import { IGetGoodsReceivedNoteResponse, IGoodsReceivedNote } from "./grn.types";
import { CLEAR_SINGLE_GATE_ENTRY } from "../gate-entry";
import { server_base_endpoints } from "../../api/server-base-endpoints";

export const FETCH_GOODS_RECEIVED_NOTE_LIST_PROGRESS =
  "FETCH_GOODS_RECEIVED_NOTE_LIST_PROGRESS";
export const FETCH_GOODS_RECEIVED_NOTE_LIST_SUCCESS =
  "FETCH_GOODS_RECEIVED_NOTE_LIST_SUCCESS";
export const FETCH_GOODS_RECEIVED_NOTE_LIST_FAILED =
  "FETCH_GOODS_RECEIVED_NOTE_LIST_FAILED";

export const fetchGoodsReceivedNoteListProgress = () =>
  action(FETCH_GOODS_RECEIVED_NOTE_LIST_PROGRESS);
export const fetchGoodsReceivedNoteListSuccess = (
  data: IGoodsReceivedNote[],
  totalRecords: number,
) => action(FETCH_GOODS_RECEIVED_NOTE_LIST_SUCCESS, { data, totalRecords });
export const fetchGoodsReceivedNoteListFailed = () =>
  action(FETCH_GOODS_RECEIVED_NOTE_LIST_FAILED);

export const fetchGoodsReceivedNoteListAsync =
  (queryParams: IQueryParams): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchGoodsReceivedNoteListProgress());
      let finalUrl = `${server_base_endpoints.grn.get_grn}${searchQuery}`;

      const res = await api.get(finalUrl);
      const data: IGoodsReceivedNote[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchGoodsReceivedNoteListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchGoodsReceivedNoteListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to fetch gate entry list",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertSingleGoodsReceivedNoteAsync =
  (
    gateEntry: IGoodsReceivedNote,
    onCallback: (isSuccess: boolean, gateEntry?: IGoodsReceivedNote) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        title: "GRN Attachment",
      };

      const {
        attachments,
        grn_no,
        prefix,
        running_no,
        combined_grn_no,
        financial_year,
        create_ts,
        insert_ts,
        ...rest
      } = gateEntry;
      const { paths } = await uploadMultipleFile(attachments, "GRN", asPayload);

      const res = await api.post(`${server_base_endpoints.grn.upsert_grn}`, {
        ...rest,
        attachments: paths,
      });
      const data = res.data.data;
      onCallback(true, data);
      dispatch(
        showMessage({
          type: "success",
          message: "GRN saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to save GRN",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_SINGLE_GOODS_RECEIVED_NOTE_PROGRESS =
  "FETCH_SINGLE_GOODS_RECEIVED_NOTE_PROGRESS";
export const FETCH_SINGLE_GOODS_RECEIVED_NOTE_SUCCESS =
  "FETCH_SINGLE_GOODS_RECEIVED_NOTE_SUCCESS";
export const FETCH_SINGLE_GOODS_RECEIVED_NOTE_FAILED =
  "FETCH_SINGLE_GOODS_RECEIVED_NOTE_FAILED";

export const fetchSingleGoodsReceivedNoteProgress = () =>
  action(FETCH_SINGLE_GOODS_RECEIVED_NOTE_PROGRESS);
export const fetchSingleGoodsReceivedNoteSuccess = (data: IGoodsReceivedNote) =>
  action(FETCH_SINGLE_GOODS_RECEIVED_NOTE_SUCCESS, { data });
export const fetchSingleGoodsReceivedNoteFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_GOODS_RECEIVED_NOTE_FAILED, { errorMessage });

export const fetchSingleGoodsReceivedNoteAsync =
  (
    gateEntryUUID: string,
    billing_company_uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSingleGoodsReceivedNoteProgress());
      let url = `${server_base_endpoints.grn.get_grn}?grn_uuid=${gateEntryUUID}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data: IGetGoodsReceivedNoteResponse[] = res.data.data;
      if (data.length > 0) {
        const { attachments, ...rest } = data[0];
        const newRespponse: IGoodsReceivedNote = produce(
          { ...rest, attachments: [] as IFileUpload[] },
          (draftState) => {
            if (Array.isArray(attachments) && attachments.length > 0) {
              draftState.attachments = attachments.map((path) => ({
                key: getUniqueId(),
                file: null,
                type: "SERVER",
                name: getFileNameFromUrl(path as unknown as string),
                path: path,
              }));
            }
          },
        );
        dispatch(fetchSingleGoodsReceivedNoteSuccess(newRespponse));
      } else {
        dispatch(
          fetchSingleGoodsReceivedNoteFailed(
            "Oops! We couldn't find any records.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchSingleGoodsReceivedNoteFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Error fetching goods received note",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_SINGLE_GOODS_RECEIVED_NOTE =
  "CLEAR_SINGLE_GOODS_RECEIVED_NOTE";
export const CLEAR_GOODS_RECEIVED_NOTE_STATE =
  "CLEAR_GOODS_RECEIVED_NOTE_STATE";

export const clearSingleGoodsReceivedNote = () =>
  action(CLEAR_SINGLE_GOODS_RECEIVED_NOTE);
export const clearGoodsReceivedNoteState = () =>
  action(CLEAR_GOODS_RECEIVED_NOTE_STATE);
